import { DocumentNode } from "@apollo/client";
import { OperationDefinitionNode } from "graphql";
import {
  FilterEntryInput,
  FilterExpressionChildInput,
  FilterExpressionOperatorEnum,
  FilterOperatorEnum,
  Maybe,
  OrderDirectionEnum,
} from "./innerTypes";
import { OrderDescription } from "./types";

export function constructOrder<T>(params?: OrderDescription<T>[]) {
  if (params?.length) {
    const orders = params
      .filter(x => x.direction !== null && x.direction !== undefined);
    return orders
      .map(({ path, direction }): { path: string, direction: OrderDirectionEnum } [] => {
        if (Array.isArray(path)) {
          return path.map(subPath => ({
            path: subPath?.toString() as string,
            direction: direction as OrderDirectionEnum,
          }));
        }
        return [ ({
          path: path?.toString() as string,
          direction: direction as OrderDirectionEnum,
        }) ];
      })
      .reduce(
        (
          acc,
          order,
        ) => [ ...acc, ...order ],
        [],
      );
  }
  return undefined;
}

export type FilterParamsType = {
  expressionBuilder?: ({ path, operator, value }: FilterEntryInput) => Maybe<FilterExpressionChildInput>,
  path: string,
  operator: FilterOperatorEnum,
  value: any
};

export function constructFilter(params?: FilterParamsType[]): Maybe<FilterExpressionChildInput> {
  if (params?.length) {
    const expressions = params
      .filter(x => {
        if (x.value === undefined || x.value === "") {
          return false;
        }
        if (typeof x.value === "number" && isNaN(x.value)) {
          return false;
        }
        return !(Array.isArray(x.value) && x.value.length === 0);
      })
      .map((filter) => {
        const expressionBuilder = filter.expressionBuilder || (({ path, operator, value }) => {
          if (Array.isArray(value) && value.some(x => x === null)) {
            const filteredValues = value.filter(x => x !== null);
            if (!filteredValues?.length) {
              return ({
                entry: {
                  path,
                  operator: FilterOperatorEnum.Null,
                },
              });
            }
            return {
              expr: {
                operator: FilterExpressionOperatorEnum.Or,
                children: [
                  {
                    entry: {
                      path,
                      operator: FilterOperatorEnum.Null,
                    },
                  },
                  {
                    entry: {
                      path,
                      operator: operator,
                      value: filteredValues,
                    },
                  },
                ],
              },
            };
          } else if (value === null) {
            return {
              entry: {
                path,
                operator: FilterOperatorEnum.Null,
              },
            };
          }
          return {
            entry: {
              path: path,
              operator: operator,
              value,
            },
          };
        });
        return expressionBuilder({
          ...filter,
          path: filter?.path?.toString(),
        });
      });

    if (expressions.length === 0) {
      return null;
    } else if (expressions.length === 1) {
      return expressions[0];
    } else {
      return {
        expr: {
          operator: FilterExpressionOperatorEnum.And,
          children: expressions,
        },
      };
    }
  }
  return null;
}

export function getDocumentsNames(...documents: DocumentNode[]) {
  return documents.map(document => {
    const operationDefinition = document.definitions.find(x => x.kind === "OperationDefinition") as OperationDefinitionNode;
    return operationDefinition?.name?.value as string;
  });
}
